/*
 * VPDB - Virtual Pinball Database
 * Copyright (C) 2019 freezy <freezy@vpdb.io>
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; either version 2
 * of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 * Modified for sigmashake-pinball by SigmaShake, 2026.
 */

import type { BumperSpec, TableInfoSpec } from "../../../shared/contracts";
import { ItemData, toVertex2D } from "../../core/item-data";
import type { Vertex2D } from "../../math/vertex2d";

export class BumperData extends ItemData {
  public center: Vertex2D;
  public radius: number = 45;
  public szCapMaterial?: string;
  public szRingMaterial?: string;
  public szBaseMaterial?: string;
  public szSkirtMaterial?: string;
  public threshold: number = 1.0;
  public force: number = 15;
  /** Degrees. */
  public scatter: number = 0;
  public heightScale: number = 90.0;
  public ringSpeed: number = 0.5;
  public orientation: number = 0.0;
  public ringDropOffset: number = 0.0;
  public szSurface?: string;
  public isCapVisible: boolean = true;
  public isBaseVisible: boolean = true;
  public isRingVisible: boolean = true;
  public isSkirtVisible: boolean = true;
  public hitEvent: boolean = true;
  public isCollidable: boolean = true;

  public constructor(itemName: string, center: Vertex2D) {
    super(itemName);
    this.center = center;
  }

  /** Builds the data from a table spec. A field that the spec leaves out keeps its upstream default. */
  public static fromSpec(spec: BumperSpec, _info?: TableInfoSpec): BumperData {
    const data = new BumperData(spec.name, toVertex2D(spec.center));
    data.radius = spec.radius;
    data.force = spec.force;
    data.threshold = spec.threshold ?? data.threshold;
    data.scatter = spec.scatter ?? data.scatter;
    data.heightScale = spec.heightScale ?? data.heightScale;
    data.orientation = spec.orientation ?? data.orientation;
    data.ringSpeed = spec.ringSpeed ?? data.ringSpeed;
    data.szCapMaterial = spec.szCapMaterial ?? spec.szMaterial;
    data.szRingMaterial = spec.szRingMaterial ?? spec.szMaterial;
    data.szBaseMaterial = spec.szBaseMaterial ?? spec.szMaterial;
    data.szSkirtMaterial = spec.szSkirtMaterial ?? spec.szMaterial;
    data.szSurface = spec.szSurface;
    data.isCollidable = spec.isCollidable ?? true;
    const visible = spec.isVisible ?? true;
    data.isCapVisible = visible;
    data.isBaseVisible = visible;
    data.isRingVisible = visible;
    data.isSkirtVisible = visible;
    return data;
  }
}
